import { useCallback, useMemo, useRef, useState } from "react";
import { MultiEditTable } from "../../common/tables/MultiEdit/MultiEditTable";
import {
  GenericVirtualizedTableCells,
  GenericVirtualizedTableFunctionRef,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { useHistory, useLocation } from "react-router-dom";
import { Alert } from "../../common/overlays/Alert/Alert";
import {
  MultiEditMappedItemObject,
  MultiEditProvider,
} from "../../common/forms/MultiEditForms/common/MultiEditProvider";
import { MultiEditInputForm } from "../../common/forms/MultiEditForms/forms/MultiEditInputForm";
import { MultiEditSelectForm } from "../../common/forms/MultiEditForms/forms/MultiEditSelectForm";
import { MultiEditHeaderWrapper } from "../../common/forms/MultiEditForms/common/MultiEditFormWrapper";
import { getValueOrDefault, useMultiEditUtils } from "../../common/forms/MultiEditForms/common/MultiEditUtils";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { BulkEditLocationState } from "../../common/tables/MultiEdit/BulkEditDropdown/BulkEditDropDownTypes";
import { Sample, SampleFieldLabels, SampleFilters, samplesConstants } from "../../api/Samples";
import { switchSamplesDefaultSortState } from "./SamplesTable";
import { projectsConstants } from "../../api/Projects";
import { ProjectsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { GenericBooleanStateProvider } from "../../common/modals/Modal/GenericModal";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { customTypeConstants } from "../../api/CustomTypes/CustomTypes";
import { CustomTypesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import { useMultiEditCustomFieldTableCells } from "../../Customization/CustomTypes/generics/useCustomTypesEntityTable";
import { useCustomTypes } from "../../Customization/CustomTypes/generics/useCustomTypes";
import {
  processInitialValuesForTypedEntity,
  processValuesForTypedEntity,
  useCustomFieldsValidationObjectShape,
} from "../../Customization/CustomTypes/generics/CustomTypeValidationUtils";
import { InferSortByOptions } from "../../api/GenericTypes";
import { getSortingFn } from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { ColumnWidthSettings } from "../../common/tables/Tabs/TableTabsTypes";

export const SamplesBulkEditTable = () => {
  const history = useHistory();
  const location = useLocation<BulkEditLocationState<Sample, SampleFilters>>();
  const defaultFilters = useMemo(() => (location && location.state?.filters) || {}, [location]);
  const initItems = useMemo(() => (location && location.state?.items) || undefined, [location]);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const functionRef = useRef<GenericVirtualizedTableFunctionRef<Sample>>(null);
  const [filters, setFilters] = useState<SampleFilters>({ ...defaultFilters });
  const [sort, setSort] = useState<SortState<InferSortByOptions<SampleFilters>>[]>(
    switchSamplesDefaultSortState(filters.sortBy)
  );
  const [typeOverride, setTypeOverride] = useState(false);
  const { types } = useCustomTypes({ entityType: "Sample", filters: { isEnabled: true } });

  // Hook to update searchTerm or orderBy if filter changes; (e.g. triggered by the sidebar)
  useMemo(() => {
    if (defaultFilters.sortBy !== undefined) {
      setSort(switchSamplesDefaultSortState(defaultFilters.sortBy));
    }
  }, [defaultFilters.sortBy]);

  const { control, getValues } = useForm<Sample>({ defaultValues: {} });

  const cfColumns = useMultiEditCustomFieldTableCells({
    types: types,
    control: control,
    getValues: getValues,
    selection: selection,
    disabled: false,
    sort,
    setSort,
    setFilters,
  });

  const columns: GenericVirtualizedTableCells<Sample> = useMemo(() => {
    let res: GenericVirtualizedTableCells<Sample> = [
      {
        id: "default-id",
        label: "ID",
        Header: "ID",
        accessor: (row) => (
          <div className={"flex row-nowrap align-center"} style={{ padding: "7px" }}>
            <span style={{ color: "var(--gray-400)" }}>
              <samp>{row.id}</samp>
            </span>
          </div>
        ),
        width: 100,
        align: "right",
        ...getSortingFn("ID", sort, setSort, setFilters),
      },
      {
        id: "default-name",
        label: "Name",
        Header: "Name",
        accessor: (row) => (
          <div className="flex" style={{ overflow: "auto", width: "100%", height: "100%" }}>
            <MultiEditInputForm id={`name`} row={row} />
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getSortingFn("NAME", sort, setSort, setFilters),
      },
      {
        id: "default-projects",
        label: "Projects",
        Header: (
          <MultiEditHeaderWrapper<Sample>
            control={control}
            getValues={getValues}
            ids={selection}
            label={SampleFieldLabels.projects}
            property="projects"
            required
            hasApply
            hasAppend
          >
            {(consolidatedValues) => (
              <ProjectsVirtualizedSelectForm
                id="projects"
                control={control}
                hasLabel={false}
                placeholder={getValueOrDefault(consolidatedValues?.projects)}
                defaultValue={consolidatedValues?.projects?.value ?? null}
                horizontal
                allowCreateEntity
                allowUnassigned
                uncontained
                // showControls
                isMulti
                filters={{ currentUserHasAddPermission: true }}
              />
            )}
          </MultiEditHeaderWrapper>
        ),
        accessor: (row, rowIndex, columnIndex) => (
          <div className="flex" style={{ overflow: "auto", width: "100%", height: "100%" }}>
            <MultiEditSelectForm id={`projects`} entityConstants={projectsConstants} row={row} isMulti required />
          </div>
        ),
        minWidth: 400,
        width: 400,
      },
      {
        id: "default-customType",
        label: "Custom Type",
        Header: (
          <MultiEditHeaderWrapper<Sample>
            control={control}
            getValues={getValues}
            ids={selection}
            label={SampleFieldLabels.customType}
            additionalControls={
              <GenericBooleanStateProvider>
                {({ isTrue: showModal, setIsTrue: setShowModal }) => (
                  <>
                    <AlertModal
                      type="success"
                      title="Confirm action"
                      description={`Switching between ${customTypeConstants.entityPlural} can lead to data loss. Please contact your system administrator if you are unsure about the implications.`}
                      showModal={showModal}
                      setShowModal={setShowModal}
                      proceedLabel={"Enable editing"}
                      // onCancel={() => setShowModal(false)}
                      onProceed={() => setTypeOverride(true)}
                      forceUserInput
                      forceUserInputText="EDIT"
                    />
                    {!typeOverride && (
                      <button className="btn btn-sm btn-success" onClick={() => setShowModal(true)}>
                        <LucideIcon name="square-pen" /> Enable editing
                      </button>
                    )}
                  </>
                )}
              </GenericBooleanStateProvider>
            }
            property="customType"
            // required
            disabled={() => !typeOverride}
            hasApply
          >
            {(consolidatedValues) => (
              <CustomTypesVirtualizedSelectForm
                id="customType"
                control={control}
                hasLabel={false}
                placeholder={getValueOrDefault(consolidatedValues?.customType)}
                defaultValue={consolidatedValues?.customType?.value ?? null}
                disabled={!typeOverride}
                horizontal
                allowUnassigned
                uncontained
                filters={{ entityTypes: ["Sample"] }}
                // showControls
              />
            )}
          </MultiEditHeaderWrapper>
        ),
        accessor: (row, rowIndex, columnIndex) => (
          <MultiEditSelectForm
            id={`customType`}
            entityConstants={customTypeConstants}
            row={row}
            required
            disabled={!typeOverride}
            filters={{ entityTypes: ["Sample"] }}
          />
        ),
        minWidth: 300,
        width: 400,
        ...getSortingFn("CUSTOM_TYPE", sort, setSort, setFilters),
      },
    ];

    if (types) {
      res = res.concat(cfColumns);
    }

    return res;
  }, [cfColumns, control, getValues, selection, sort, typeOverride, types]);

  const columnWidths = useMemo(
    (): ColumnWidthSettings => Object.fromEntries(columns.map((col) => [col.id, col.width])),
    [columns]
  );
  const columnSettings = useMemo(
    (): ColumnsSettings<Sample> =>
      Object.fromEntries(
        columns.map((col, idx) => [
          col.id,
          {
            pos: idx,
            active: true,
            header: col.label ?? col.id,
          },
        ])
      ),
    [columns]
  );

  const onSelectionChange = useCallback((selection: Set<number>) => {
    setSelection(selection);
  }, []);

  const onSuccessCallback = useCallback(
    (data: Sample[]) => {
      const _data = Object.fromEntries(Object.entries(data).map(([, d]) => [d.id, d]));
      history.replace({ state: { ...location.state, items: _data } });
    },
    [history, location.state]
  );

  const beforeSubmit = useCallback(
    (data: MultiEditMappedItemObject<Sample>) => {
      return Object.fromEntries(
        Object.entries(data).map(([id, d]) => [
          id,
          {
            ...d,
            ...(d.customType && types && processValuesForTypedEntity(d, types?.[d.customType.id])),
          },
        ])
      );
    },
    [types]
  );

  const beforeInit = useCallback(
    (data: MultiEditMappedItemObject<Sample>) => {
      return Object.fromEntries(
        Object.entries(data).map(([id, d]) => [
          id,
          {
            ...d,
            customFields: {},
            ...(d.customType && types && processInitialValuesForTypedEntity(d, types?.[d.customType.id])),
          },
        ])
      );
    },
    [types]
  );

  const { onSubmit, loading } = useMultiEditUtils<Sample>({
    entityConstants: samplesConstants,
    showToast: true,
    onSuccessCallback: onSuccessCallback,
    beforeSubmit: beforeSubmit,
    beforeReset: beforeInit,
  });

  const { conditionalTypeFormSchema } = useCustomFieldsValidationObjectShape();

  const conditionalSampleFormSchema = useMemo(
    () => ({
      name: yup.string().required("A sample name is required"),
      projects: yup.array().ensure().min(1, "At least one project is required"),
      ...conditionalTypeFormSchema,
    }),
    [conditionalTypeFormSchema]
  );

  if (initItems && !Object.keys(initItems).length && !Object.keys(defaultFilters).length)
    return <Alert type="info" message="Bulk editing is only available to a prior selection" fit centered />;
  return (
    <MultiEditProvider<Sample>
      resource={samplesConstants.resource}
      initItems={initItems}
      ids={defaultFilters.ids ?? []}
      entityValidationSchema={conditionalSampleFormSchema}
      beforeInit={beforeInit}
      isCustomSchemaEntity
      canAbort
    >
      {({ consolidatedValues, items, consolidatedCustomFieldValues, customFieldPlaceholders }) => {
        return (
          <MultiEditTable<Sample, SampleFilters>
            entityConstants={samplesConstants}
            columns={columns}
            filters={filters}
            defaultSelection={defaultFilters.ids ?? undefined}
            onSelectionChange={onSelectionChange}
            onSubmit={onSubmit}
            loading={loading}
            functionRef={functionRef}
            columnWidths={columnWidths}
            defaultColumnSettings={columnSettings}
          />
        );
      }}
    </MultiEditProvider>
  );
};
